/**
 * What this account has chosen: how it wants bookmarks summarised and tagged, and what
 * the save popup should start with.
 *
 * ── Typed fields, and not a blob in `user_ui_settings` ────────────────────────
 *
 * That store is explicitly the *toolkit's* opaque blobs behind an allowlist, and its
 * own header states that no LunaPin module parses one — which is exactly what keeps a
 * lunula upgrade from being a change to this database. These values are LunaPin's own,
 * and more importantly the **server** has to read them: the enrichment worker decides
 * what to ask a model for from [AccountSettings.aiSummaryLength] and
 * [AccountSettings.aiLanguage], and a server parsing a blob written by a browser is
 * precisely the coupling `UiSettings.sq` exists to avoid.
 *
 * ── One state for "never chose" and "chose the default" ───────────────────────
 *
 * An account that has never written gets these defaults rather than a 404, and the
 * server stores nothing until it is asked to. That is the opposite of
 * [UiSettingsState.settings], where an absent key deliberately means "never chosen" and
 * an empty value means "chosen, and blank" — and the difference is that there, blank is
 * something a user can pick. Here every field has exactly one meaningful default and
 * nobody can choose "unset", so two spellings of one state would be a branch at every
 * read site for no fact anyone could act on.
 *
 * **The defaults live here and only here.** `UserSettings.sq` deliberately declares no
 * column defaults: the store writes every column on every write, so the constructor
 * arguments below are the single copy. A `DEFAULT` in SQL would be a second one, free
 * to disagree, and reachable only through a code path — the never-written account —
 * that no test of the wire type would exercise.
 *
 * @see ApiRoutes.USER_SETTINGS
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * How long a generated summary should be.
 *
 * Strings rather than an enum, and the reason is where a bad value is caught. A
 * `@Serializable enum` refuses an unknown value inside kotlinx-serialization, so the
 * route would answer a generic "Malformed request." for a typo in one field — while a
 * string reaches the store, which refuses it with a sentence naming the field and
 * listing what it will accept. The wire is not the place this rule is enforced; the
 * store is, so that every client meets the same one.
 *
 * The sets are here rather than on the server so that a settings screen can render the
 * options from the same list the server validates against, and so a fourth value is one
 * edit rather than two that can drift.
 */
object SummaryLengths {
    /** A single sentence. */
    const val ONE_LINE: String = "one-line"

    /** A couple of sentences — the default, and what a list row can show. */
    const val SHORT: String = "short"

    /** A paragraph, for somebody who wants the summary instead of the page. */
    const val DETAILED: String = "detailed"

    /** Every accepted value, in the order a screen should offer them. */
    val all: List<String> = listOf(ONE_LINE, SHORT, DETAILED)
}

/** What language a generated summary should be written in. See [SummaryLengths]. */
object SummaryLanguages {
    /**
     * Whatever the page itself is written in — the default.
     *
     * The honest one for a collection that mixes languages: a summary of a Swedish
     * article reads best in Swedish, and nothing has to be configured for that to be
     * true.
     */
    const val PAGE: String = "page"

    /** English, whatever the page is in. */
    const val ENGLISH: String = "en"

    /** Swedish, whatever the page is in. */
    const val SWEDISH: String = "sv"

    /** Every accepted value, in the order a screen should offer them. */
    val all: List<String> = listOf(PAGE, ENGLISH, SWEDISH)
}

/**
 * The most new tags one bookmark may be given in a single enrichment.
 *
 * **A safety valve, not a budget somebody chose.** It used to be the maximum of a slider
 * — *New tags per bookmark*, 0 to 10 — and that setting is gone, because a per-bookmark
 * allowance is a proxy for the number that actually matters and never states it: 3 new
 * tags across 5653 bookmarks authorises a vocabulary of roughly seventeen thousand, and
 * nobody setting it to 3 means that. What replaced it is
 * [AccountSettings.aiTagVocabularyTarget], which says the real thing.
 *
 * What is left here is the per-call cap, and it earns its keep on its own: a strange page
 * — a link farm, a glossary, a table of contents — can come back with forty plausible
 * words, and there is no reading of any ceiling under which one bookmark should spend it
 * in one go. Ten because that is what the removed slider's maximum was, and no evidence
 * has ever suggested a bookmark needs an eleventh new tag.
 */
const val MAX_NEW_TAGS_PER_BOOKMARK: Int = 10

/**
 * How many distinct tags this account wants its collection to settle at.
 *
 * The vocabulary ceiling: the number of *tags*, account-wide, not the number of tags on a
 * bookmark. Two things read it — the enrichment worker, which stops handing out new tags
 * to bookmarks that are already filed once the collection is at the ceiling, and the tidy
 * tool, which is how somebody gets back under one.
 *
 * ── Why these five, and why a ChoiceRow rather than a number field ────────────
 *
 * Measured against a real 5653-bookmark collection carrying 1182 tags: keeping only the
 * top 100 covers 99% of tagged bookmarks, and the average number of bookmarks sharing a
 * tag-combination moves only from 1.66 to 1.87. The extra 1082 tags buy almost no ability
 * to pinpoint anything. So the interesting range is small, it is logarithmic, and the
 * difference between 180 and 200 is not a difference anybody can perceive — which is
 * exactly the shape a handful of presets fits and a spinner does not.
 *
 * [NO_LIMIT] is last rather than first because it is the opt-out, and an opt-out offered
 * first reads as the recommendation.
 */
object TagVocabularyTargets {
    /**
     * No ceiling at all — the pre-LPN-46 behaviour, kept as a choice.
     *
     * Zero, and it means *unbounded* rather than *no tags*: a target of zero tags would be
     * a collection that cannot be filed, which is not a thing to offer. The gate reads this
     * value by name and gives such an account infinite room; it never subtracts it. Turning
     * tagging off entirely is [AccountSettings.aiTagsEnabled], one screen up.
     */
    const val NO_LIMIT: Int = 0

    /**
     * The largest ceiling the store will take.
     *
     * Not one of the presets and not reachable from the settings window — it exists so that
     * a scripted client sending 2_000_000_000 is refused with a sentence rather than
     * silently storing a number that means the same as [NO_LIMIT] while claiming to be a
     * limit. Ten thousand is an order of magnitude above the largest collection this
     * project has ever been measured against, so it refuses nothing anybody wants.
     */
    const val MAX: Int = 10_000

    /** Every value the settings window offers, in the order it draws them. */
    val all: List<Int> = listOf(50, 100, 200, 400, NO_LIMIT)
}

/**
 * Everything the server holds for this account, plus the one fact about the deployment
 * that a settings screen cannot work without.
 *
 * @property aiAvailable whether this *deployment* has a model provider key at all —
 *   `LUNAPIN_KEY_OPENAI`, read at boot; see the server's `AiConfig`. **The only thing
 *   any caller is ever told about that key.** Not its value, not a prefix, not its
 *   length, and there is no route that returns it: the key is the operator's, it is
 *   never stored anywhere, and there is no field to type it into. A settings screen
 *   reads this to decide between showing the AI switches and showing a read-only row
 *   naming the variable.
 *
 *   Deliberately *not* part of [SetAccountSettingsRequest]. It is not this account's to
 *   choose, and a writable field would be a field a client could disagree with the
 *   server about.
 * @property aiSummariesEnabled whether a saved bookmark should be summarised. On by
 *   default: an instance whose operator configured a key wants the feature, and an
 *   account that does not can say so in one press.
 * @property aiTagsEnabled whether a saved bookmark should be tagged. Tags a model
 *   applies are **applied, never proposed** — nothing waits for review — and their
 *   origin is recorded on the row, so switching this off later leaves what was already
 *   filed alone.
 * @property aiTagVocabularyTarget how many distinct tags this account wants to end up
 *   with, one of [TagVocabularyTargets.all] from a settings screen and anything from 0 to
 *   [TagVocabularyTargets.MAX] over the wire. [TagVocabularyTargets.NO_LIMIT] — zero —
 *   means no ceiling.
 *
 *   **A target, not a hard stop.** A bookmark that nothing established files still gets
 *   new tags when the collection is at the ceiling, because the alternative is a
 *   collection that can never learn a subject it has not saved before; see
 *   `EnrichmentWorker.tagsToApply`, which is where the rule is written down and the one
 *   place it is enforced.
 * @property aiSummaryLength one of [SummaryLengths.all].
 * @property aiLanguage one of [SummaryLanguages.all].
 * @property saveQuickTags the handful of tags this account files under often enough that
 *   typing them is the wrong amount of work. **Two surfaces read this**, and both of them
 *   are "put these within reach": the save popup offers them as one-press chips, and a
 *   bookmark list's tag rail puts them first, ahead of whatever its sort would otherwise
 *   have led with. The second is what stops this being a setting about one window — a tag
 *   worth a chip when saving is the same tag worth finding again first.
 *
 *   Normalised exactly as a bookmark's tags are — trimmed, `#`-prefixed, lower-cased,
 *   de-duplicated and sorted (see [Bookmark.tags]) — because a chip that spelled a tag
 *   differently from the field beside it would file into a second tag that looks the same.
 *
 *   **Every [saveDefaultTags] entry is also one of these, and the store enforces it rather
 *   than trusting a client to.** The two lists are one control with two states — offered,
 *   and offered-and-already-on — so a default that was not offered would be a tag applied
 *   to every save with no chip to turn it off for the one save you did not want it on. That
 *   is the shape SheetMark's `displayedTagsWhenAdding` / `selectedTagsWhenAdding` pair
 *   settled on, and the invariant is the whole of why the pair works.
 * @property saveDefaultTags tags the save popup starts with, already filled in — a subset
 *   of [saveQuickTags], and their chips come up already pressed so a save that should not
 *   carry one is one press away rather than an edit afterwards.
 *   Normalised exactly as a bookmark's tags are — trimmed, `#`-prefixed, lower-cased,
 *   de-duplicated and sorted (see [Bookmark.tags]) — because a default tag and a typed
 *   tag have to be the same thing or the popup would offer a tag that files nothing.
 *
 * ── Two settings that were here and are not ──────────────────────────────────
 *
 * `saveCloseAfter` and `saveWarnDuplicate`, both booleans, both removed rather than
 * defaulted-and-hidden. Neither was ever read: the popup has always closed itself two
 * seconds after a save (see the server's `Bookmarklet.kt`, where the timer is
 * unconditional) and has always been silent about a re-save. So they were two switches
 * that moved a value in a database and changed nothing on any screen, which is worse than
 * either behaviour they claimed to choose between.
 *
 * What replaced them is not a setting. Closing after a save is what the popup does, full
 * stop — a window opened by "save this", which has saved it, has nothing left to be. And a
 * URL the collection already had now gets a *different sentence* on that same self-closing
 * notice, because saving is an upsert: the fact worth surfacing is which of the two things
 * just happened, and a fact does not need a switch.
 *
 * ── And a third: the pinned model ────────────────────────────────────────────
 *
 * `aiModel` was a free-text name for the model to ask, `""` meaning "whatever the build
 * considers its default". Unlike the two above it *was* read — the enrichment worker and
 * the test route both took it — so this is a different argument.
 *
 * It went because it was the one deployment fact this type let an account choose. The key
 * beside it is the operator's: read from the environment at boot, never stored, with no
 * field to type it into and a whole section of `AiTestRoutes` explaining why. Which model
 * that key is spent on is the same kind of fact — it decides what a summary costs and how
 * good it is, against the operator's quota — and having one of the pair be a text box any
 * signed-in account could edit was the inconsistency. It was also unvalidated by design,
 * because provider model names come and go faster than this server is deployed, so its
 * realistic failure was a near-miss name that stopped every enrichment silently until
 * somebody thought to press *Test connection*.
 *
 * What replaced it is `OpenAiSummaryProvider`'s `DEFAULT_MODEL` for everybody — which is
 * exactly what `""` already meant, and what every account that never typed in the box was
 * already getting. An operator who wants a different one edits that constant; the day that
 * is not enough, the answer is an environment variable beside `LUNAPIN_KEY_OPENAI`, not a
 * field on a settings screen.
 *
 * ── And a fourth: new tags per bookmark ──────────────────────────────────────
 *
 * `aiNewTagsPerBookmark`, 0–10, behind a row of eleven buttons on the AI tab. Replaced
 * rather than removed: [aiTagVocabularyTarget] is the same intent stated as the number it
 * was always a proxy for. See [MAX_NEW_TAGS_PER_BOOKMARK], which survives as the per-call
 * safety valve, and `EnrichmentWorker.tagsToApply` for what now decides whether a bookmark
 * may coin a tag at all.
 *
 * One thing it could express and the replacement cannot: **0, "never invent a tag"**, the
 * way somebody keeps a vocabulary curated entirely by hand while still having it applied.
 * The nearest thing now is a ceiling the collection is already at, which is not the same
 * promise — a bookmark nothing established files still gets tags. That is a deliberate
 * trade and not an oversight: the setting was measured being used at its default by the
 * only collection there is, and a hard "never" is a feature to add back when somebody
 * wants it rather than a fifth preset nobody asked for.
 */
@Serializable
data class AccountSettings(
    val aiAvailable: Boolean = false,
    val aiSummariesEnabled: Boolean = true,
    val aiTagsEnabled: Boolean = true,
    // 200, which is two ceilings above the 100 that covered 99% of the measured
    // collection. Deliberately generous: the default has to be a number nobody meets by
    // accident on the way to deciding they care, because the first time this setting is
    // noticed should be when somebody opens the Tags tab, not when a tag they wanted
    // silently failed to be coined.
    val aiTagVocabularyTarget: Int = 200,
    val aiSummaryLength: String = SummaryLengths.SHORT,
    val aiLanguage: String = SummaryLanguages.PAGE,
    val saveQuickTags: List<String> = emptyList(),
    val saveDefaultTags: List<String> = emptyList(),
)

/**
 * The defaults, so that the request below cannot spell them differently.
 *
 * The two types carry the same fields and would carry the same literals; a second copy of
 * a default is the kind of thing that stays right until somebody changes one of them.
 * Referencing an [AccountSettings] built with no arguments is the cheapest way to make the
 * pair impossible to desynchronise.
 */
private val DEFAULTS = AccountSettings()

/**
 * Store all of them.
 *
 * **The whole set in one request**, unlike [SetUiSettingRequest] which writes one key at
 * a time. The reason is the writer: the toolkit persists a theme as three independent
 * `Persister.write` calls and nothing may batch them, whereas these are a form somebody
 * fills in and applies — so one request is one intent, and a half-applied set is not a
 * state anybody asked for.
 *
 * Every field has the same default as its [AccountSettings] counterpart, so a client
 * that omits one is asking for the default rather than being refused. That makes this
 * safe for an older client to send, and it makes "reset to defaults" an empty object.
 *
 * `aiAvailable` is absent on purpose — see [AccountSettings.aiAvailable].
 */
@Serializable
data class SetAccountSettingsRequest(
    val aiSummariesEnabled: Boolean = DEFAULTS.aiSummariesEnabled,
    val aiTagsEnabled: Boolean = DEFAULTS.aiTagsEnabled,
    val aiTagVocabularyTarget: Int = DEFAULTS.aiTagVocabularyTarget,
    val aiSummaryLength: String = DEFAULTS.aiSummaryLength,
    val aiLanguage: String = DEFAULTS.aiLanguage,
    val saveQuickTags: List<String> = DEFAULTS.saveQuickTags,
    val saveDefaultTags: List<String> = DEFAULTS.saveDefaultTags,
)
